import {
  ALPHANUM, ALPHANUM_EXTENDED,
  CLUSTER_NAME,
  KUBERNETES_CLUSTER_NAME, NETWORK_NAME,
  SECURITY_GROUP_NAME,
  STORAGE_POLICY_NAME
} from '@/common/utils/validate';
import i18n from '@/common/i18n';
import {
  commonVolumeNameRule,
  common2to255Rule,
  common2to64Rule,
  commonNameTo64Rule
} from '@/common/validation/commonValidationRules';
import computedLocale from '@/common/utils/computedLocale';
import { DISALLOWED_STORAGE_CLUSTER_NAMES } from '@/common/constants/clusters.constants';

export const requiredNameRule = computedLocale(() => ({
  type: 'string',
  required: true,
  message: i18n.t('Specify a name'),
  transform: item => item.trim()
}));

export const nameMaxLengthRule = computedLocale(() => ({
  max: 255,
  message: i18n.t('Name must be up to 255 characters long')
}));

export const nameMinMaxLengthRule = (min, max) => computedLocale(() => ({
  min,
  max,
  message: i18n.t('A name must be {min} to {max} characters long.', { min, max })
}));

export const nameAlphanumRule = computedLocale(() => ({
  pattern: ALPHANUM_EXTENDED,
  message: i18n.t('Only alphanumeric values are allowed')
}));

export const descriptionMaxLengthRule = computedLocale(() => ({
  max: 255,
  message: i18n.t('Description must be up to 255 characters long')
}));

export const domainNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a domain')
}));

export const clusterNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a cluster name')
}));

export const clusterNameLengthRule = computedLocale(() => ({
  min: 3,
  max: 46,
  message: i18n.t('The cluster name must be 3 to 46 characters long')
}));

export const clusterNamePatternRule = computedLocale(() => ({
  type: 'string',
  pattern: CLUSTER_NAME,
  message: i18n.t('The cluster name may only contain Latin letters, numbers, and hyphens. It must start with a letter and end with a letter or number.')
}));

export const clusterNameProhibitedNameRule = computedLocale(() => ({
  validator: (rule, value, callback) => callback(DISALLOWED_STORAGE_CLUSTER_NAMES.includes(value) ? [i18n.t('Cannot use this name for the cluster')] : [])
}));

export const k8sClusterNameLengthRule = computedLocale(() => ({
  type: 'string',
  pattern: KUBERNETES_CLUSTER_NAME,
  message: i18n.t('A name must be 2 to 255 characters long and start with a letter. It may only contain letters, numbers, periods, hyphens, and underscores.')
}));

export const placementNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a placement name'),
  transform: name => name.trim()
}));

export const loginNameRule = computedLocale(() => ({
  type: 'string',
  required: true,
  message: i18n.t('Login is required')
}));

export const loginNameLengthRule = computedLocale(() => ({
  min: 1,
  max: 255,
  message: i18n.t('Login must be up to 255 characters long')
}));

export const policyNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a policy')
}));

export const policyNameLengthRule = computedLocale(() => ({
  type: 'string',
  pattern: STORAGE_POLICY_NAME,
  message: i18n.t('A policy name must be 2 to 64 characters long. It may only contain Latin letters, numbers, periods, colons, spaces, hyphens, and underscores.')
}));

export const flavorNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a flavor name'),
  transform: item => item.trim()
}));

export const networkNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a network name')
}));

export const networkNameLengthRule = computedLocale(() => ({
  pattern: NETWORK_NAME,
  message: i18n.t('A network name must be 3 to 32 characters long. It may only contain Latin letters, numbers and underscores')
}));

export const routerNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a router name'),
  transform: item => item.trim()
}));

export const securityGroupRule = computedLocale(() => ({
  pattern: SECURITY_GROUP_NAME,
  message: i18n.t('The default security group already exists')
}));

export const imageNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify an image name'),
  transform: item => item.trim()
}));

export const loadBalancerNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a balancer name')
}));

export const productTitleRule = computedLocale(() => ({
  max: 60,
  type: 'string',
  message: i18n.t('Max. 60 characters')
}));

export const projectNameRule = computedLocale(() => ({
  type: 'string',
  required: true,
  message: i18n.t('Project name is required')
}));

export const storagePolicyNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Choose a storage policy')
}));

export const virtualMachineNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a virtual machine name'),
  transform: name => name?.trim()
}));

export const userNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Specify a user name')
}));

export const snapshotNameRule = computedLocale(() => ({
  required: true,
  message: i18n.t('Snapshot name is required')
}));

export const flavorNameRules = ({ flavorNames }) => ([
  flavorNameRule,
  common2to255Rule,
  ...(flavorNames ? [{
    validator: (rule, value, callback) => {
      const errors = [];

      if (flavorNames.includes(value)) {
        errors.push(i18n.t('Flavor "{name}" already exists', { name: value }));
      }
      callback(errors);
    }
  }] : [])
]);

export const imageNameRules = [
  imageNameRule,
  common2to255Rule
];

export const loadBalancerNameRules = ({ getItem, loadBalancerId }) => ([
  loadBalancerNameRule,
  common2to255Rule,
  {
    validator: (rule, value, callback) => {
      const loadBalancer = getItem && typeof getItem === 'function' ? getItem(value) : null;

      !loadBalancer || loadBalancer.id === loadBalancerId ? callback([]) : callback([i18n.t('Load balancer "{0}" already exists', [value])]);
    }
  }
]);

export const loginNameRules = [
  loginNameRule,
  loginNameLengthRule
];

export const kubernetesClusterNameRules = [
  clusterNameRule,
  k8sClusterNameLengthRule
];

export const nameRules = [
  requiredNameRule,
  nameMaxLengthRule,
  nameAlphanumRule
];

export const placementNameRules = [
  placementNameRule,
  common2to255Rule
];

export const policyNameRules = [
  policyNameRule,
  policyNameLengthRule,
];

export const projectNameRules = [
  projectNameRule,
  commonNameTo64Rule
];

export const rolesetNameRules = [
  networkNameRule,
  networkNameLengthRule
];

export const routerNameRules = [
  routerNameRule,
  common2to64Rule
];

export const securityGroupNameRules = [
  ...nameRules,
  securityGroupRule
];

export const storageClusterNameRules = [
  clusterNameRule,
  clusterNameLengthRule,
  clusterNamePatternRule,
];

export const trafficTypeNameRules = [
  computedLocale(() => ({ required: true, message: i18n.t('Specify a traffic type name') })),
  computedLocale(() => ({ min: 3, max: 32, message: i18n.t('Must be from {from} to {to} characters long', { from: 3, to: 32 }) })),
  computedLocale(() => ({ pattern: ALPHANUM, message: i18n.t('Only alphanumeric values are allowed') }))
];

export const virtualMachineNameRules = [
  virtualMachineNameRule,
  common2to255Rule
];

export const volumeNameRules = [
  commonVolumeNameRule,
  common2to64Rule
];

export const workerGroupNameRules = [
  computedLocale(() => ({ required: true, message: i18n.t('Specify a worker group name') })),
  computedLocale(() => ({
    type: 'string',
    pattern: KUBERNETES_CLUSTER_NAME,
    message: i18n.t('A name must be 2 to 255 characters long and start with a letter. It may only contain letters, numbers, periods, hyphens, and underscores.')
  })),
];
